/**
 * Trésorerie, besoin en fonds de roulement, et passage de l'un à l'autre.
 *
 * Un résultat n'est pas une trésorerie. Un investissement de 30 000 € coûte
 * 30 000 € de trésorerie l'année de l'acquisition et 6 000 € de résultat
 * pendant cinq ans ; une cotisation appelée en décembre et réglée en mars pèse
 * sur la trésorerie sans rien changer au résultat. Sans cette distinction, les
 * deux se confondent à l'écran et l'arbitrage se fait à l'aveugle.
 *
 *   Δtrésorerie = résultat + amortissements − ΔBFR − investissements
 *
 * **Limite assumée** : aucune source disponible ne porte les dettes
 * fournisseurs. Le besoin en fonds de roulement calculé ici est donc **minoré**,
 * et chaque structure qu'il produit porte le drapeau `minore` afin que
 * l'interface le signale au lieu de le taire.
 */
import { PLANCHER_TRESORERIE } from './configuration.js';
import { sommeExacte } from './adherents.js';
import { seuil } from './scenarios.js';
/** Préfixe des comptes de tiers sociaux au plan comptable général. */
const PREFIXE_COMPTES_SOCIAUX = '43';
/**
 * Solde des comptes de tiers sociaux à la clôture d'un exercice.
 *
 * Un solde créditeur est une dette : les cotisations dues aux organismes
 * sociaux et non encore réglées. Le cumul court depuis l'origine des données,
 * puisqu'un solde de bilan n'est pas un flux d'exercice.
 */
export function dettesSociales(ecritures, exercice) {
    const fin = `${exercice}-12`;
    const lignes = ecritures.filter((e) => e.comptePcg.startsWith(PREFIXE_COMPTES_SOCIAUX) && e.mois <= fin);
    const solde = sommeExacte(lignes.map((e) => e.credit - e.debit));
    return Math.max(0, solde);
}
/**
 * Besoin en fonds de roulement **d'ouverture**, mesuré et non reconstitué.
 *
 * À la clôture d'un exercice, ce que le syndicat possède hors banque moins ce
 * qu'il doit hors banque est exactement l'écart entre ses fonds propres et ses
 * disponibilités — le syndicat ne porte pas d'immobilisation significative.
 *
 * C'est la seule mesure de BFR disponible qui soit une position **de fin
 * d'exercice**. Les créances constatées en cours d'année n'en sont pas une :
 * au 30 juillet, les cotisations ne sont pas toutes appelées et les subventions
 * pas toutes versées, si bien qu'un ratio calculé à cette date décrit un moment
 * du calendrier et non un besoin structurel.
 */
export function bfrDOuverture(fondsPropresCloture, disponibilites) {
    return sommeExacte([fondsPropresCloture, -disponibilites]);
}
/**
 * Ratio de besoin en fonds de roulement, rapporté aux produits de l'exercice.
 *
 * C'est le « BFR en pourcentage du chiffre d'affaires » de la pratique
 * courante : un seul inducteur, mesurable, et dont l'hypothèse — des délais de
 * règlement qui ne se dégradent pas — se vérifie d'un exercice à l'autre.
 */
export function ratioBfr(bfrOuverture, produitsReference) {
    return produitsReference > 0 ? bfrOuverture / produitsReference : 0;
}
/**
 * Série de besoin en fonds de roulement, projetée par le ratio.
 *
 * `bfrOuverture` est la position mesurée à la clôture précédente : sans elle,
 * la variation du premier exercice vaudrait la totalité du BFR et écraserait
 * la trésorerie d'ouverture d'autant.
 */
export function besoinFondsRoulement(e) {
    let precedent = e.bfrOuverture;
    return e.assiettes.map((a) => {
        const bfr = Math.round(a.produits * e.ratio * 100) / 100;
        const variation = sommeExacte([bfr, -precedent]);
        precedent = bfr;
        return {
            exercice: a.exercice,
            produits: a.produits,
            bfr,
            variation,
            minore: true,
        };
    });
}
export function decomposerBfr(e) {
    return {
        ...e,
        net: sommeExacte([e.creancesCotisations, e.creancesSubventions, -e.dettesSociales]),
        minore: true,
    };
}
/** Cascade résultat → trésorerie, exercice par exercice. */
export function passageResultatTresorerie(e) {
    let ouverture = e.tresorerieOuverture;
    return e.exercices.map((x) => {
        const variationBfr = e.bfr.find((b) => b.exercice === x.exercice)?.variation ?? 0;
        const variation = sommeExacte([
            x.resultat,
            x.amortissements,
            -variationBfr,
            -x.investissements,
        ]);
        const cloture = sommeExacte([ouverture, variation]);
        const s = seuil(x.exercice, e.seuils);
        const ligne = {
            exercice: x.exercice,
            tresorerieOuverture: ouverture,
            resultat: x.resultat,
            amortissements: x.amortissements,
            variationBfr,
            investissements: x.investissements,
            variationTresorerie: variation,
            tresorerieCloture: cloture,
            plancher: PLANCHER_TRESORERIE,
            seuil: s,
            sousSeuil: cloture < s,
            minore: true,
        };
        ouverture = cloture;
        return ligne;
    });
}
/**
 * Trésorerie mensuelle constatée d'un exercice, reconstituée des mouvements
 * bancaires. Les mois postérieurs à la date d'arrêté ne sont pas produits :
 * une trésorerie mensuelle est un constat, pas une projection.
 */
export function tresorerieMensuelle(ecritures, exercice, soldeOuverture, dateArrete) {
    const debut = `${exercice}-01-01`;
    const periode = ecritures.filter((e) => e.date >= debut && e.date <= dateArrete);
    // Une date d'arrêté antérieure à l'exercice ne produit aucun mois ; une date
    // postérieure les produit tous. Sans ces deux bornes, un arrêté du
    // 31 décembre de l'exercice précédent rendrait douze mois vides.
    const nbMois = dateArrete < debut ? 0 : dateArrete > `${exercice}-12-31` ? 12 : Number(dateArrete.slice(5, 7));
    let solde = soldeOuverture;
    return Array.from({ length: Math.max(0, Math.min(12, nbMois)) }, (_, i) => {
        const mois = `${exercice}-${String(i + 1).padStart(2, '0')}`;
        const duMois = periode.filter((e) => e.date.slice(0, 7) === mois);
        const encaissements = sommeExacte(duMois.filter((e) => e.sens === 'debit').map((e) => e.montant));
        const decaissements = sommeExacte(duMois.filter((e) => e.sens === 'credit').map((e) => e.montant));
        const variation = sommeExacte([encaissements, -decaissements]);
        solde = sommeExacte([solde, variation]);
        return { mois, encaissements, decaissements, variation, solde };
    });
}
//# sourceMappingURL=tresorerie.js.map